import {
    Component,
    type ComponentFactory,
    ComponentFactoryResolver,
    type ComponentRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    ViewContainerRef,
    inject
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { TileConfig, TileData } from '../../../interfaces/Tile.interface';
import { TilesStaticProvider } from '../../../providers/tiles/tiles.static.provider';
import type { SpawnableTile } from '../spawnable-tile/spawnable-tile.component';

@UntilDestroy()
@Component({
    styleUrls: ['./tile-spawner.scss'],
    selector: 'tile-spawner',
    templateUrl: 'tile-spawner.html'
})
export class TileSpawnerComponent {
    private resolver = inject(ComponentFactoryResolver);
    private _tilesStaticProvider = inject(TilesStaticProvider);

    @ViewChild('spawn', { static: true, read: ViewContainerRef }) container!: ViewContainerRef;

    private _spawnedTile!: ComponentRef<SpawnableTile<TileData>>;

    private _tileConfig: TileConfig = {};
    private _tileData!: TileData;

    @Input() set tileData(data: TileData) {
        this._tileData = data;
        // If a tile is already spawned, we need to unspawn it before spawning the new one
        if (this._spawnedTile) {
            this._spawnedTile.destroy();
        }
        this.spawnNewTile();
    }

    @Input() set tileConfig(config: TileConfig) {
        this._tileConfig = config;
        if (this._spawnedTile) {
            this._spawnedTile.instance.tileConfig = this._tileConfig;
        }
    }

    @Output() deleted = new EventEmitter<void>();
    @Output() edited = new EventEmitter<TileData>();

    spawnNewTile() {
        if (!this._tileData) return;
        const tileProvider = this._tilesStaticProvider.getTileProvider(this._tileData.tileType);
        if (!tileProvider) return;
        const classConstructor = tileProvider.getTileComponent();
        if (!classConstructor) return;

        const factory: ComponentFactory<SpawnableTile<TileData>> =
            this.resolver.resolveComponentFactory<SpawnableTile<TileData>>(classConstructor);
        const component = this.container.createComponent(factory);
        this._spawnedTile = component;

        component.instance.tileData = this._tileData;
        component.instance.tileConfig = this._tileConfig;

        this.subscribeToEvents();
    }

    subscribeToEvents() {
        this._spawnedTile.instance.deleted.pipe(untilDestroyed(this)).subscribe(() => {
            this.deleted.next();
        });

        this._spawnedTile.instance.edited.pipe(untilDestroyed(this)).subscribe(newTileData => {
            this.edited.next(newTileData);
        });
    }
}
